/**
 * tracking.js — camada de tracking da loja (storefront).
 *
 * Responsabilidades:
 *   1. Apanhar os IDs de clique do URL (fbclid, gclid, …) e guardá-los em
 *      cookie first-party, para sobreviverem à navegação e ao checkout.
 *   2. Gerar e manter o external_id (UUID próprio, 1º party).
 *   3. Gerar um event_id por evento e disparar browser + servidor com o MESMO id
 *      — é isto que impede a Meta de contar o mesmo evento duas vezes.
 *
 * Não contém tokens. O token da CAPI vive na função /api/track.
 */
(function () {
  'use strict';

  var ENDPOINT = window.SL_TRACK_ENDPOINT || '/api/track';
  var DIAS = 90;

  // ------------------------------------------------------------- cookies

  function lerCookie(nome) {
    var m = document.cookie.match('(^|;)\\s*' + nome + '\\s*=\\s*([^;]+)');
    return m ? decodeURIComponent(m[2]) : null;
  }

  function escreverCookie(nome, valor, dias) {
    var d = new Date();
    d.setTime(d.getTime() + (dias || DIAS) * 86400000);
    // Domínio raiz para o cookie sobreviver entre www e subdomínios
    var raiz = location.hostname.split('.').slice(-2).join('.');
    document.cookie = nome + '=' + encodeURIComponent(valor) +
      ';expires=' + d.toUTCString() + ';path=/;domain=.' + raiz + ';SameSite=Lax';
  }

  // ---------------------------------------------- IDs de clique e atribuição

  var parametros = new URLSearchParams(location.search);

  /**
   * O _fbc tem um formato próprio: fb.1.<timestamp>.<fbclid>.
   * Se o fbevents.js ainda não o criou, criamo-lo nós — senão perdemos a
   * atribuição de quem entra e só compra dias depois.
   */
  function garantirFbc() {
    var existente = lerCookie('_fbc');
    if (existente) return existente;
    var fbclid = parametros.get('fbclid');
    if (!fbclid) return null;
    var valor = 'fb.1.' + Date.now() + '.' + fbclid;
    escreverCookie('_fbc', valor, DIAS);
    return valor;
  }

  // Guardar UTMs e IDs de clique para os podermos ler no checkout
  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term',
   'gclid', 'gbraid', 'wbraid', 'ttclid'].forEach(function (chave) {
    var v = parametros.get(chave);
    if (v) escreverCookie('_sl_' + chave, v, DIAS);
  });

  /** external_id: UUID nosso, estável por browser. Vai hasheado para a Meta. */
  function garantirExternalId() {
    var id = lerCookie('_sl_eid');
    if (id) return id;
    id = (crypto && crypto.randomUUID)
      ? crypto.randomUUID()
      : 'sl-' + Date.now() + '-' + Math.random().toString(36).slice(2, 12);
    escreverCookie('_sl_eid', id, 365);
    return id;
  }

  /** client_id do GA4, se o gtag existir nesta loja. Formato: GA1.1.<id>.<ts> */
  function clientIdGa() {
    var ga = lerCookie('_ga');
    if (!ga) return null;
    var partes = ga.split('.');
    return partes.length >= 4 ? partes.slice(-2).join('.') : null;
  }

  garantirFbc();
  var EXTERNAL_ID = garantirExternalId();

  // ------------------------------------------------------------- event_id

  function novoEventId(evento) {
    var aleatorio = (crypto && crypto.randomUUID)
      ? crypto.randomUUID().slice(0, 18)
      : Math.random().toString(36).slice(2, 14);
    return evento + '.' + Date.now() + '.' + aleatorio;
  }

  // --------------------------------------------------------------- disparo

  /**
   * slTrack('AddToCart', { valor: 29.9, content_ids: ['123'] }, { email: '…' })
   *
   * Dispara em paralelo:
   *   - browser, via fbq (rápido, apanha o cookie de 1ª parte)
   *   - servidor, via /api/track (sobrevive a adblockers e ao iOS)
   * Ambos com o mesmo event_id → a Meta fica só com um.
   */
  function slTrack(evento, dados, utilizador) {
    dados = dados || {};
    utilizador = utilizador || {};

    var eventId = dados.event_id || novoEventId(evento);

    // --- 1. browser ---------------------------------------------------
    if (typeof window.fbq === 'function') {
      var padrao = ['PageView', 'ViewContent', 'Search', 'AddToCart',
                    'InitiateCheckout', 'AddPaymentInfo', 'Purchase',
                    'ViewCategory', 'Lead', 'CompleteRegistration'];
      var metodo = padrao.indexOf(evento) !== -1 ? 'track' : 'trackCustom';
      var conteudoBrowser = {
        currency: dados.moeda || 'EUR',
        value: dados.valor,
        content_ids: dados.content_ids,
        content_type: dados.content_type,
        content_name: dados.content_name,
        content_category: dados.content_category,
        contents: dados.contents,
        num_items: dados.num_items,
        search_string: dados.search_string,
      };
      window.fbq(metodo, evento, conteudoBrowser, { eventID: eventId });
    }

    // --- 2. servidor --------------------------------------------------
    var corpo = {
      evento: evento,
      event_id: eventId,
      timestamp: Date.now(),
      url: location.href,
      action_source: 'website',
      dados: dados,
      utilizador: Object.assign({
        fbp: lerCookie('_fbp'),
        fbc: lerCookie('_fbc'),
        external_id: EXTERNAL_ID,
        client_id: clientIdGa(),
        user_agent: navigator.userAgent,
      }, utilizador),
    };

    try {
      // keepalive para o pedido sobreviver à navegação (ex.: clique no checkout)
      fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(corpo),
        keepalive: true,
        mode: 'cors',
      }).catch(function () { /* nunca partir a loja por causa do tracking */ });
    } catch (e) { /* idem */ }

    return eventId;
  }

  /**
   * Guarda os identificadores nos atributos do carrinho.
   *
   * É isto que faz o Purchase do webhook `orders/paid` ter `_fbp`, `_fbc` e
   * `external_id`. O webhook corre no servidor da Shopify para o nosso — não
   * tem browser, não tem cookies. Se não os deixarmos aqui, a compra chega à
   * Meta sem identificadores de browser e a qualidade da correspondência cai.
   */
  var atributosGuardados = false;
  function guardarIdentificadoresNoCarrinho() {
    if (atributosGuardados) return;
    atributosGuardados = true;
    var atributos = {
      _fbp: lerCookie('_fbp') || '',
      _fbc: lerCookie('_fbc') || '',
      _sl_eid: EXTERNAL_ID,
      _sl_ua: navigator.userAgent,
      _sl_url: location.origin + location.pathname,
    };
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach(function (k) {
      var v = lerCookie('_sl_' + k);
      if (v) atributos[k] = v;
    });
    try {
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: atributos }),
      }).catch(function () { atributosGuardados = false; });
    } catch (e) { atributosGuardados = false; }
  }

  /**
   * Conta uma visualização de produto, uma vez por sessão e por produto.
   * O travão é aqui e não no servidor: sem ele, cada refresh contava outra vez
   * e o número deixava de querer dizer nada.
   */
  function contarVisualizacao(handle) {
    if (!handle) return;
    var chave = 'sl_visto_' + handle;
    try {
      if (sessionStorage.getItem(chave)) return;
      sessionStorage.setItem(chave, '1');
    } catch (e) { /* modo privado: conta na mesma, sem travão */ }

    try {
      fetch(ENDPOINT.replace('/api/track', '/api/ver-produto'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ handle: handle }),
        keepalive: true,
      }).catch(function () {});
    } catch (e) {}
  }

  // ------------------------------------------------------ API pública

  window.slTrack = slTrack;
  window.slGuardarIdentificadores = guardarIdentificadoresNoCarrinho;
  window.slContarVisualizacao = contarVisualizacao;
  window.slTrackUtil = {
    lerCookie: lerCookie,
    externalId: EXTERNAL_ID,
    clientIdGa: clientIdGa,
    novoEventId: novoEventId,
  };
})();
